import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  CalendarIcon,
  CarFrontIcon,
  CheckCircle2Icon,
  CheckIcon,
  CopyPlusIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  RotateCcwIcon,
  ShoppingCartIcon,
  StickyNoteIcon,
  StoreIcon,
  Trash2Icon,
  TruckIcon,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList } from "@/components/app/attachments";
import { LinksPanel } from "@/components/app/links";
import { Markdown } from "@/components/app/markdown";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { PartPicker } from "@/components/app/part-picker";
import { OrderStatusBadge, WorkOrderStatusBadge } from "@/components/app/status";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Segmented, Skeleton } from "@/components/ui/misc";
import { AddToOrderDialog } from "@/features/orders/add-to-order-dialog";
import { QuickPurchaseDialog } from "@/features/orders/quick-purchase-dialog";
import { PartSheet } from "@/features/parts/part-sheet";
import { PartsListDialog } from "@/features/parts-list/parts-list-dialog";
import { CompleteDialog } from "@/features/work-orders/complete-dialog";
import { RequirementList } from "@/features/work-orders/requirement-list";
import { WorkOrderSheet } from "@/features/work-orders/work-order-sheet";
import { useFormat } from "@/lib/format";
import { usePriceBasis } from "@/lib/prices";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/work-orders/$workOrderId")({
  component: WorkOrderPage,
});

type WorkOrder = RouterOutputs["workOrders"]["get"];

function WorkOrderPage() {
  const { workOrderId } = Route.useParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const priceBasis = usePriceBasis();
  const navigate = useNavigate();
  const { data: wo, isLoading } = useQuery(trpc.workOrders.get.queryOptions({ id: workOrderId }));
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [listing, setListing] = useState(false);
  const [buying, setBuying] = useState(false);
  const [creatingPart, setCreatingPart] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"delete" | "reopen" | null>(null);

  const setStatus = useMutation(trpc.workOrders.setStatus.mutationOptions());
  const addRequirement = useMutation(trpc.workOrders.addRequirement.mutationOptions());
  const updateRequirement = useMutation(trpc.workOrders.updateRequirement.mutationOptions());
  const removeRequirement = useMutation(trpc.workOrders.removeRequirement.mutationOptions());
  const switchToLinked = useMutation(trpc.workOrders.useLinkedPart.mutationOptions());
  const reopen = useMutation(trpc.workOrders.reopen.mutationOptions());
  const remove = useMutation(trpc.workOrders.delete.mutationOptions());
  const saveTemplate = useMutation(trpc.workOrders.saveAsTemplate.mutationOptions());

  if (isLoading || !wo) {
    return (
      <Page>
        <Skeleton className="mb-6 h-12 w-96" />
        <Skeleton className="h-80 rounded-xl" />
      </Page>
    );
  }

  const completed = wo.status === "completed";
  const missingItems = wo.requirements
    .filter((r) => r.availability && r.availability.missing > r.inDraft)
    .map((r) => ({
      partId: r.partId,
      name: r.partName,
      unit: r.unit,
      qty: (r.availability?.missing ?? 0) - r.inDraft,
      workOrderId: wo.id,
    }));
  // A parts list starts with every part at its required amount (or what was used, once done).
  const listItems = wo.requirements.map((r) => ({
    partId: r.partId,
    name: r.partName,
    unit: r.unit,
    primaryNumber: r.primaryNumber,
    qty: completed ? (r.qtyUsed ?? r.qty) : r.qty,
    missingQty: r.availability?.missing ?? 0,
    workOrderIds: [wo.id],
    hint: r.consumesStock ? null : t("workOrders.tool"),
    selected: true,
  }));
  // New lines start at one unit; the amount is edited inline.
  const add = (partId: string) =>
    addRequirement.mutate({
      workOrderId: wo.id,
      data: { partId, qty: 1000, consumesStock: true, note: null },
    });

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: t("nav.workOrders"), to: "/work-orders" }]}
        title={wo.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link
              to="/vehicles/$vehicleId"
              params={{ vehicleId: wo.vehicle.id }}
              className="flex items-center gap-1 hover:text-foreground"
            >
              <CarFrontIcon className="size-3.5" />
              {wo.vehicle.name}
            </Link>
            <span>{t(`workOrderType.${wo.type}`)}</span>
            {wo.plannedOn && !completed ? (
              <span className="flex items-center gap-1">
                <CalendarIcon className="size-3.5" />
                {format.date(wo.plannedOn)}
              </span>
            ) : null}
          </span>
        }
        actions={
          <>
            {!completed && wo.status !== "cancelled" ? (
              <Segmented
                aria-label={t("common.status")}
                value={wo.status as "idea" | "planned" | "in_progress"}
                onChange={(status) =>
                  setStatus.mutate(
                    { id: wo.id, status },
                    {
                      onSuccess: () =>
                        toast.success(
                          t("workOrders.statusChanged", { status: t(`workOrderStatus.${status}`) }),
                        ),
                    },
                  )
                }
                options={(["idea", "planned", "in_progress"] as const).map((s) => ({
                  value: s,
                  label: t(`workOrderStatus.${s}`),
                }))}
              />
            ) : (
              <WorkOrderStatusBadge status={wo.status} />
            )}
            {!completed && wo.status !== "cancelled" ? (
              <Button onClick={() => setCompleting(true)}>
                <CheckIcon />
                {t("workOrders.complete")}
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  <PencilIcon />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    saveTemplate.mutate(
                      { id: wo.id, title: null },
                      {
                        onSuccess: (templateId) =>
                          toast.success(t("workOrders.savedAsTemplate"), {
                            action: {
                              label: t("common.view"),
                              onClick: () =>
                                void navigate({
                                  to: "/templates/$templateId",
                                  params: { templateId },
                                }),
                            },
                          }),
                      },
                    )
                  }
                >
                  <CopyPlusIcon />
                  {t("workOrders.saveAsTemplate")}
                </DropdownMenuItem>
                {completed ? (
                  <DropdownMenuItem onSelect={() => setConfirm("reopen")}>
                    <RotateCcwIcon />
                    {t("workOrders.reopen")}
                  </DropdownMenuItem>
                ) : wo.status === "cancelled" ? (
                  <DropdownMenuItem
                    onSelect={() => setStatus.mutate({ id: wo.id, status: "planned" })}
                  >
                    <RotateCcwIcon />
                    {t("workOrderStatus.planned")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onSelect={() => setStatus.mutate({ id: wo.id, status: "cancelled" })}
                  >
                    <XCircleIcon />
                    {t("workOrderStatus.cancelled")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => setConfirm("delete")}>
                  <Trash2Icon />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {completed ? (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-ok/30 bg-ok-bg px-4 py-3 text-ok-fg sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <CheckCircle2Icon className="size-5" />
            {wo.mileageAtCompletion !== null
              ? t("workOrders.completedBannerMileage", {
                  date: format.date(wo.completedOn),
                  mileage: format.mileage(wo.mileageAtCompletion, wo.vehicle.mileageUnit),
                })
              : t("workOrders.completedBanner", { date: format.date(wo.completedOn) })}
          </div>
          <Button variant="outline" size="sm" onClick={() => setConfirm("reopen")}>
            <RotateCcwIcon />
            {t("workOrders.reopen")}
          </Button>
        </div>
      ) : wo.availability && wo.requirements.length > 0 && wo.status !== "cancelled" ? (
        <ReadinessBanner wo={wo} onOrder={() => setOrdering(true)} onBuy={() => setBuying(true)} />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="flex min-w-0 flex-col gap-7">
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle>
                {t("workOrders.parts")}
                <span className="ml-2 font-normal text-muted-foreground tabular">
                  {wo.requirements.length || ""}
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                {wo.requirements.length ? (
                  <Button variant="ghost" size="sm" onClick={() => setListing(true)}>
                    <PrinterIcon />
                    {t("partsList.print")}
                  </Button>
                ) : null}
                {!completed ? (
                  <PartPicker
                    excludeIds={[]}
                    fitKey={wo.vehicle.chassisCode?.key}
                    onSelect={(part) => add(part.id)}
                    onCreate={(name) => setCreatingPart(name)}
                    trigger={
                      <Button variant="outline" size="sm">
                        <PlusIcon />
                        {t("workOrders.addPart")}
                      </Button>
                    }
                  />
                ) : null}
              </div>
            </CardHeader>
            {wo.requirements.length ? (
              <div className="border-t border-border">
                <RequirementList
                  lines={wo.requirements}
                  readOnly={completed}
                  qtyLabel={completed ? t("workOrders.used") : t("figures.required")}
                  onUpdate={(id, data) => updateRequirement.mutate({ id, data })}
                  onRemove={(id) => removeRequirement.mutate({ id })}
                  onUseLinked={(id, partId, name) =>
                    switchToLinked.mutate(
                      { id, partId },
                      { onSuccess: () => toast.success(t("workOrders.usingInstead", { name })) },
                    )
                  }
                />
              </div>
            ) : (
              <CardContent>
                <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("workOrders.partsEmpty")}
                </p>
              </CardContent>
            )}
          </Card>

          <section>
            <SectionTitle
              action={
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                  <PencilIcon />
                  {t("common.edit")}
                </Button>
              }
            >
              {t("common.description")}
            </SectionTitle>
            {wo.description ? (
              <Card className="p-5">
                <Markdown>{wo.description}</Markdown>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">{t("parts.noDescription")}</p>
            )}
          </section>

          <div className="grid gap-7 md:grid-cols-2">
            <section>
              <SectionTitle>{t("common.links")}</SectionTitle>
              <LinksPanel ownerType="work_order" ownerId={wo.id} links={wo.links} />
            </section>
            <section>
              <SectionTitle>{t("common.documents")}</SectionTitle>
              <DocumentList ownerType="work_order" ownerId={wo.id} attachments={wo.attachments} />
            </section>
          </div>
        </div>

        <aside className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {completed ? t("workOrders.partsCost") : t("workOrders.estimatedCost")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tracking-tight tabular">
                {format.money(wo.cost.totalMinor, wo.cost.currency)}
              </div>
              <p className="text-xs text-muted-foreground">{priceBasis}</p>
              {wo.cost.unpricedLines ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("workOrders.unpriced", { count: wo.cost.unpricedLines })}
                </p>
              ) : null}
            </CardContent>
          </Card>
          {wo.orderLines.length ? (
            <Card>
              <CardHeader>
                <CardTitle>{t("workOrders.orderedFor")}</CardTitle>
              </CardHeader>
              <div className="divide-y divide-border border-t border-border">
                {wo.orderLines.map((line) => {
                  const req = wo.requirements.find((r) => r.partId === line.partId);
                  return (
                    <Link
                      key={line.lineId}
                      to="/orders/$orderId"
                      params={{ orderId: line.orderId }}
                      className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm transition hover:bg-muted/50"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {req?.partName ?? line.orderName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {line.orderName}
                        </div>
                      </div>
                      <OrderStatusBadge status={line.orderStatus} />
                    </Link>
                  );
                })}
              </div>
            </Card>
          ) : null}
          {wo.template ? (
            <Link
              to="/templates/$templateId"
              params={{ templateId: wo.template.id }}
              className="flex items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-sm transition hover:bg-muted/50"
            >
              <StickyNoteIcon className="size-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t("workOrders.template")}:</span>
              <span className="truncate font-medium">{wo.template.title}</span>
            </Link>
          ) : null}
          <p className="px-1 text-xs text-muted-foreground">
            {t("common.created")} {format.date(wo.createdAt)}
          </p>
        </aside>
      </div>

      <WorkOrderSheet open={editing} onOpenChange={setEditing} workOrder={wo} />
      <CompleteDialog open={completing} onOpenChange={setCompleting} workOrder={wo} />
      <AddToOrderDialog open={ordering} onOpenChange={setOrdering} items={missingItems} />
      <PartsListDialog
        open={listing}
        onOpenChange={setListing}
        items={listItems}
        context={`${wo.title} · ${wo.vehicle.name}`}
        missingFilter={!completed}
      />
      <QuickPurchaseDialog
        open={buying}
        onOpenChange={setBuying}
        initialLines={listItems
          .filter((item) => item.missingQty > 0)
          .map((item) => ({ ...item, qty: item.missingQty }))}
      />
      <PartSheet
        open={creatingPart !== null}
        onOpenChange={(open) => !open && setCreatingPart(null)}
        initialName={creatingPart ?? undefined}
        onSaved={(id) => add(id)}
      />
      <ConfirmDialog
        open={confirm === "reopen"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("workOrders.reopen")}
        description={t("workOrders.reopenBody")}
        confirmLabel={t("workOrders.reopen")}
        pending={reopen.isPending}
        onConfirm={() =>
          reopen.mutate(
            { id: wo.id },
            {
              onSuccess: () => {
                setConfirm(null);
                toast.success(t("workOrders.reopened"));
              },
            },
          )
        }
      />
      <ConfirmDialog
        open={confirm === "delete"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("common.deleteConfirmTitle", { name: wo.title })}
        description={completed ? t("workOrders.deleteBody") : undefined}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { id: wo.id },
            {
              onSuccess: () => {
                toast.success(t("workOrders.deleted"));
                void navigate({ to: "/work-orders" });
              },
            },
          )
        }
      />
    </Page>
  );
}

function ReadinessBanner({
  wo,
  onOrder,
  onBuy,
}: {
  wo: WorkOrder;
  onOrder: () => void;
  /** Records parts bought on the spot, e.g. at a local dealer. */
  onBuy: () => void;
}) {
  const { t } = useTranslation();
  const a = wo.availability;
  if (!a) return null;
  const missingLines = wo.requirements.filter((r) => r.availability && r.availability.missing > 0);
  const coveredByDrafts =
    missingLines.length > 0 &&
    missingLines.every((r) => r.inDraft >= (r.availability?.missing ?? 0));
  const config = {
    ready: {
      icon: CheckCircle2Icon,
      className: "border-ok/30 bg-ok-bg text-ok-fg",
      title: t("readinessLong.ready"),
      body: t("readinessLong.readyBody", { count: a.lines }),
    },
    waiting: {
      icon: TruckIcon,
      className: "border-transit/30 bg-transit-bg text-transit-fg",
      title: t("readinessLong.waiting"),
      body: t("readinessLong.waitingBody", { count: a.onOrder }),
    },
    missing: {
      icon: AlertCircleIcon,
      className: "border-missing/30 bg-missing-bg text-missing-fg",
      title: t("readinessLong.missing"),
      body: coveredByDrafts
        ? t("readinessLong.draftsBody")
        : t("readinessLong.missingBody", { count: a.missing + a.partial }),
    },
  }[a.readiness];
  return (
    <div
      className={cn(
        "mb-6 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
        config.className,
      )}
    >
      <div className="flex items-start gap-3">
        <config.icon className="mt-0.5 size-5 shrink-0" />
        <div>
          <div className="text-sm font-semibold">{config.title}</div>
          <div className="text-sm opacity-90">{config.body}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {a.onOrder ? (
          <Badge tone="transit">
            {t("availability.on_order")}: {a.onOrder}
          </Badge>
        ) : null}
        {a.readiness === "missing" ? (
          <Button size="sm" variant="outline" onClick={onBuy}>
            <StoreIcon />
            {t("orders.quickPurchase")}
          </Button>
        ) : null}
        {a.readiness === "missing" && coveredByDrafts ? (
          <Button size="sm" variant="outline" asChild>
            <Link to="/orders">
              <TruckIcon />
              {t("nav.orders")}
            </Link>
          </Button>
        ) : a.readiness === "missing" ? (
          <Button size="sm" onClick={onOrder}>
            <ShoppingCartIcon />
            {t("workOrders.orderMissing")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
